import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { MattersActions } from '@/matter/store/slice'
import { UserFilesPollingActions } from '@/user_files/polling/store/slice'
import { selectUploadFilesByServerFileId } from './selectors'
import { UppyFileUploadActions } from './slice'

export const uppyFileUploadListenerMiddleware = createListenerMiddleware()
const startListener = uppyFileUploadListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Reset the per-matter drawer UI when the active matter changes.
 *
 * Upload rows themselves survive the switch — they are scoped by `matterId` and filtered out of
 * the other matter's drawer — but the drawer's dismissed/minimised state belongs to the matter the
 * user dismissed it in.
 *
 * `setActiveMatterId` is dispatched idempotently (MatterLayout re-syncs the URL on every
 * render pass), so only act when the id actually changed.
 */
startListener({
  actionCreator: MattersActions.setActiveMatterId,
  effect: (action, listenerApi) => {
    const previousMatterId = listenerApi.getOriginalState().mattersState.activeMatterId
    if (previousMatterId === action.payload.matterId) return

    listenerApi.dispatch(UppyFileUploadActions.activeMatterChanged())
  },
})

startListener({
  actionCreator: UserFilesPollingActions.pollBatchReceived,
  effect: async (action, listenerApi) => {
    const uploadFilesByServerId = selectUploadFilesByServerFileId(listenerApi.getState())
    const requestedUploadFileIds = new Set(action.payload.requestedIds.filter((fileId) => uploadFilesByServerId[fileId] !== undefined))
    if (requestedUploadFileIds.size === 0) return

    const returnedIds = new Set(Object.keys(action.payload.files))

    for (const [fileId, fileData] of Object.entries(action.payload.files)) {
      if (!requestedUploadFileIds.has(fileId)) continue

      listenerApi.dispatch(
        UppyFileUploadActions.backendDataReceived({
          serverFileId: fileId,
          record: fileData.record,
          reductoJob: fileData.reducto_job,
          assets: fileData.assets,
        })
      )
    }

    for (const requestedId of action.payload.requestedIds) {
      if (!requestedUploadFileIds.has(requestedId) || returnedIds.has(requestedId)) continue

      listenerApi.dispatch(
        UppyFileUploadActions.backendDataMissing({
          serverFileId: requestedId,
          error: 'Backend did not return file data (file may be missing or not owned)',
        })
      )
    }
  },
})
